import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import type { IActionCategory } from 'src/types/actions';
import type { IResourceProvider } from 'src/types/api';
import type {
  IAiModel,
  IModelTierConfig,
  ModelSettings,
  StringKeys
} from 'src/types/base';
import { RESOURCE_PROVIDERS, VIEW_TYPE } from 'src/types/enum';
import type {
  CustomInstructions,
  IGetIdentityProviderResponse
} from 'src/types/settings';
import type { IRole } from 'src/types/user';

export type Permission = {
  created_by: string;
  created_on: string;
  desc_id: number;
  description: string;
  modified_by: string;
  modified_on: string;
  name: string;
  rid: number;
  role: string;
  type_id: number;
  value: boolean;
};

// feature_perms rows are pre-flattened server-side to just the resolved
// boolean, keyed by "<resource_code>.<permission_code>" - see
// User.fetch_feature_perms on the backend.
export type FeaturePermission = {
  value: boolean;
};

export type IconResource = {
  id: number;
  icon: string;
  category?: string;
  sub_category?: string;
};

export type UserFavourites = {
  id: number;
  created_on: string;
  modified_on: string;
  item_id: number;
  username: string;
  item_type: string;
  tenant_id: number;
  app_id: number;
  name: string;
  display_name: string;
  initials: string;
  initials_color: string;
  provider_type: string | null;
  code: string;
  type_id: number;
  [z: string]: any;
};

export type ServiceClientProps = {
  kc_token_exchange_authentication_url: string;
  kc_token_exchange_authorization_url: string;
  kc_token_exchange_client_id: string;
  kc_token_exchange_client_secret: string;
  is_service_client_created: boolean;
  kc_token_exchange_wps_conn_url: string;
  kc_token_exchange_step: string;
  kc_token_exchange_chat_stream: string;
  kc_token_exchange_status: string;
  kc_token_exchange_token_refresh_rate: string;
  kc_token_exchange_logout_url: string;
};

type DataReducer = CustomInstructions & {
  language: string;
  isLangInitialized: boolean;
  allKCRoles: string[];
  allKCRolesDetails: IRole[];
  isLoadingRoles: boolean;
  uploadTransactionId: string;
  documents: {
    [id: number]: {
      file: Blob;
    };
  };
  lookups: { [lookup_type: string]: any[] };
  userAppPermissions: { [key: string]: Permission };
  userFeaturePermissions: { [key: string]: FeaturePermission };
  applicationResources: StringKeys;
  userResourceProviders: IResourceProvider[];
  userIdentityProviders: IGetIdentityProviderResponse['data'];
  sharePointProviderId: number;
  oneDriveProviderId: number;
  confluenceProviderId: number;
  googleDriveProviderId: number;
  userFavourites: UserFavourites[];
  isOnboarded: boolean;
  configuredModels: IAiModel[];
  modelSettings: ModelSettings;
  areModelsInitialised: boolean;
  chatDefaultModel: IAiModel;
  isFavouritesFetching: boolean;
  icons: IconResource[];
  iconMap: Record<number, string>;
  userDataLibraryViewPreference: string;
  modelTiersRateLimit: IModelTierConfig;
  userDocAnalystOnboarding: boolean;
  externalApiCredentials?: ServiceClientProps;
  orgLogo: {
    signInLogo?: string;
    expandedNavbarLogo?: string;
    collapsedNavbarLogo?: string;
    unifiedLogo?: string;
  };
  defaultIcons: IconResource[];
  actionsData: IActionCategory[];
  isCompanyInfo: boolean;

  docAnalystAnalysisCount: number | null;
  dataAnalystQueryCount: number | null;
  searchPlusCount: number | null;
  smartMeetCount: number | null;
};

type ApplicationResources = {
  applicationResources: StringKeys;
};

const initialState: DataReducer = {
  language: 'en',
  isLangInitialized: false,
  allKCRoles: [],
  allKCRolesDetails: [],
  isLoadingRoles: true,
  uploadTransactionId: '',
  documents: {},
  lookups: {},
  userAppPermissions: {},
  userFeaturePermissions: {},
  applicationResources: { User: '' },
  userResourceProviders: [],
  userIdentityProviders: [],
  sharePointProviderId: 0,
  oneDriveProviderId: 0,
  confluenceProviderId: 0,
  googleDriveProviderId: 0,
  userFavourites: [],
  isOnboarded: false,
  configuredModels: null,
  modelSettings: {},
  areModelsInitialised: false,
  chatDefaultModel: null,
  isFavouritesFetching: false,
  icons: [],
  iconMap: {},
  isCustomInstructionsEnabled: false,
  userPersonalization: '',
  responseCustomization: '',
  userDataLibraryViewPreference: VIEW_TYPE.GRID,
  modelTiersRateLimit: undefined,
  userDocAnalystOnboarding: false,
  externalApiCredentials: {
    kc_token_exchange_authentication_url: '',
    kc_token_exchange_authorization_url: '',
    kc_token_exchange_client_id: '',
    kc_token_exchange_client_secret: '',
    is_service_client_created: false,
    kc_token_exchange_wps_conn_url: '',
    kc_token_exchange_step: '0',
    kc_token_exchange_chat_stream: 'true',
    kc_token_exchange_status: 'disabled',
    kc_token_exchange_token_refresh_rate: '',
    kc_token_exchange_logout_url: ''
  },
  orgLogo: {
    signInLogo: '',
    expandedNavbarLogo: '',
    collapsedNavbarLogo: '',
    unifiedLogo: ''
  },
  defaultIcons: [],
  actionsData: [],
  isCompanyInfo: false,
  docAnalystAnalysisCount: null,
  dataAnalystQueryCount: null,
  searchPlusCount: null,
  smartMeetCount: null
};

const GlobalData = createSlice({
  name: 'data',
  initialState,
  reducers: {
    updateLookups(state, action) {
      state.lookups = action.payload;
    },
    updateUserLanguage(state, action) {
      state.language = action.payload || state.language;
      state.isLangInitialized = true;
    },
    updateDocAnalystAnalysisCount(state, action) {
      state.docAnalystAnalysisCount =
        action.payload || state.docAnalystAnalysisCount;
    },
    updateDataAnalystQueryCount(state, action) {
      state.dataAnalystQueryCount =
        action.payload || state.dataAnalystQueryCount;
    },
    updateSearchPlusCount(state, action) {
      state.searchPlusCount = action.payload || state.searchPlusCount;
    },
    updateSmartMeetCount(state, action) {
      state.smartMeetCount = action.payload || state.smartMeetCount;
    },
    updateUserCustomInstructions(
      state,
      action: PayloadAction<CustomInstructions>
    ) {
      state.isCustomInstructionsEnabled =
        action.payload.isCustomInstructionsEnabled;
      state.userPersonalization = action.payload.userPersonalization;
      state.responseCustomization = action.payload.responseCustomization;
    },
    updateUserOnboarding(state, action) {
      state.isOnboarded = action.payload || state.isOnboarded;
    },
    updateChatDefaultModel(state, { payload }: { payload: IAiModel }) {
      state.chatDefaultModel = payload || state.chatDefaultModel;
    },
    updateConfiguredModels(state, action) {
      state.configuredModels =
        action.payload?.sort((a, b) => a.priority - b.priority) ||
        state.configuredModels;
      state.areModelsInitialised = true;
    },
    setAllKCRole(state, action: PayloadAction<IRole[]>) {
      let rolesList: string[] = action.payload.map((item: any) => item.name);
      state.allKCRoles = rolesList;
      state.allKCRolesDetails = action.payload;
      state.isLoadingRoles = false;
    },
    updateUploadTransactionId(state, { payload }) {
      state.uploadTransactionId = payload;
    },
    addDocument(
      state,
      { payload }: { payload: { id: number | string; file: Blob } }
    ) {
      state.documents[payload.id] = { file: payload.file };
    },
    removeDocument(state, { payload }) {
      delete state.documents[payload];
    },
    setUserAppPermissions(state, { payload }) {
      state.userAppPermissions = payload;
    },
    setUserFeaturePermissions(state, { payload }) {
      state.userFeaturePermissions = payload;
    },
    setApplicationResources(
      state: ApplicationResources,
      action: PayloadAction<{
        applicationResources: StringKeys;
      }>
    ) {
      const { applicationResources } = action.payload;
      state.applicationResources = applicationResources;
    },
    setUserResourceProvider(state, { payload }) {
      state.userResourceProviders = payload;
      state.sharePointProviderId = +payload.find(
        ({ type, is_configuration_completed }) =>
          type === RESOURCE_PROVIDERS.SHAREPOINT && is_configuration_completed
      )?.id;
      state.oneDriveProviderId = +payload.find(
        ({ type, is_configuration_completed }) =>
          type === RESOURCE_PROVIDERS.ONEDRIVE && is_configuration_completed
      )?.id;
      state.confluenceProviderId = +payload.find(
        ({ type, is_configuration_completed }) =>
          type === RESOURCE_PROVIDERS.CONFLUENCE && is_configuration_completed
      )?.id;
      state.googleDriveProviderId = +payload.find(
        ({ type, is_configuration_completed }) =>
          type === RESOURCE_PROVIDERS.GOOGLEDRIVE && is_configuration_completed
      )?.id;
    },
    setUserIdentityProviders(state, { payload }) {
      state.userIdentityProviders = payload;
    },
    setUserFavourites(state, { payload }) {
      state.userFavourites = payload;
      state.isFavouritesFetching = false;
    },
    setIsFavouritesFetching(state, { payload }) {
      state.isFavouritesFetching = payload;
    },
    setIcons(state, { payload }) {
      state.icons = payload;
      state.iconMap = Object.fromEntries(
        (payload as IconResource[]).map((icon) => [icon.id, icon.icon])
      );
    },
    setModelSettings(state, action: PayloadAction<ModelSettings>) {
      state.modelSettings = action.payload;
    },
    setUserDataLibraryViewPreference(state, { payload }) {
      state.userDataLibraryViewPreference = payload;
    },
    updateModelTiersRateLimit(state, action: PayloadAction<IModelTierConfig>) {
      state.modelTiersRateLimit = action.payload;
    },
    setUserDocAnalystOnboarding(state, action: PayloadAction<boolean>) {
      state.userDocAnalystOnboarding = action.payload;
    },
    setExternalApiCredentials(
      state,
      action: PayloadAction<ServiceClientProps>
    ) {
      state.externalApiCredentials = action.payload;
    },
    setOrgLogo(
      state,
      action: PayloadAction<{
        signInLogo?: string;
        expandedNavbarLogo?: string;
        collapsedNavbarLogo?: string;
        unifiedLogo?: string;
      }>
    ) {
      state.orgLogo = action.payload;
    },
    setDefaultIcons(state, action: PayloadAction<IconResource[]>) {
      state.defaultIcons = action.payload;
    },
    setActionsData(state, action: PayloadAction<IActionCategory[]>) {
      state.actionsData = action.payload || state.actionsData;
    },
    updateUserContactInfo(state, action) {
      state.isCompanyInfo = action.payload || state.isCompanyInfo;
    }
  }
});

export default GlobalData;
export const reducer = GlobalData.reducer;
export const {
  updateLookups,
  updateUserLanguage,
  updateUserOnboarding,
  updateUserCustomInstructions,
  updateConfiguredModels,
  updateChatDefaultModel,
  updateUploadTransactionId,
  setAllKCRole,
  addDocument,
  removeDocument,
  setUserAppPermissions,
  setUserFeaturePermissions,
  setApplicationResources,
  setUserResourceProvider,
  setUserIdentityProviders,
  setUserFavourites,
  setIsFavouritesFetching,
  setIcons,
  setModelSettings,
  setUserDataLibraryViewPreference,
  updateModelTiersRateLimit,
  setUserDocAnalystOnboarding,
  setExternalApiCredentials,
  setOrgLogo,
  setDefaultIcons,
  setActionsData,
  updateUserContactInfo,
  updateDocAnalystAnalysisCount,
  updateDataAnalystQueryCount,
  updateSearchPlusCount,
  updateSmartMeetCount
} = GlobalData.actions;
